import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ApiAuthError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button, Input, Select, Spinner } from '../atoms';
import { useToast } from '../organisms';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { useIsCompact } from '../lib/useMediaQuery';
import { FormBuilderPanel } from './FormBuilder';

/**
 * FormsLibrary — /forms  (Domain 11.4 + 11.5)
 *
 * Browse/select the statutory form library and generate a merged, downloadable
 * PDF for a real parcel. Fully wired to the REAL backend — no mocks:
 *   • GET  /api/form-templates/library?state_code&type   → the state form library
 *     (services/form-render.listFormTemplates: TX AoA/NoP, GA PT-311A/Notice).
 *   • GET  /api/parcels?q                                 → parcel picker (the
 *     merge entity; forms are entity_type='parcel').
 *   • POST /api/form-templates/:id/render {entity_type,entity_id} → live text
 *     preview with the {{token}} merge + unresolved-field report.
 *   • POST /api/form-templates/:id/generate {entity_type,entity_id} → 11.5 persist
 *     step: render → PDF → R2 → documents row; returns { document_id, view_url }.
 *     The PDF is served through the guarded GET /api/documents/:id/download.
 *
 * Gated to firm staff by the backend (requireStaff on the /api/form-templates
 * router); the client/consultant portals never route here.
 */

interface FormTemplate {
  id: string;
  state_code: string;
  type: string;
  version: number;
  entity_type: string;
  name: string;
}

interface RenderResult {
  template_id: string;
  state_code: string | null;
  name: string;
  type: string | null;
  entity_type: string;
  rendered_content: string;
  unresolved: string[];
  document_id: string | null;
}

interface GenerateResult {
  document_id: string;
  view_url: string;
  filename: string;
  name: string;
  state_code: string | null;
  type: string | null;
  unresolved: string[];
}

interface Parcel {
  id: string;
  account_number: string;
  name: string | null;
  owner_name: string | null;
  county: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  authorization: 'Authorization',
  appeal: 'Appeal',
  notice: 'Notice',
  rendition: 'Rendition',
};

function typeVariant(type: string): 'info' | 'success' | 'warning' | 'neutral' {
  if (type === 'appeal') return 'info';
  if (type === 'authorization') return 'success';
  if (type === 'notice') return 'warning';
  return 'neutral';
}

export function FormsLibrary() {
  const { toast } = useToast();
  // GM4 — two surfaces share this route: the statutory {{token}} library (default)
  // and the PDF Form Builder (upload a fillable PDF, map fields, generate, deliver).
  const [view, setView] = useState<'library' | 'builder'>('library');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [parcelSearch, setParcelSearch] = useState('');
  const debouncedSearch = useDebouncedValue(parcelSearch, 300);
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null);
  const [preview, setPreview] = useState<RenderResult | null>(null);
  const [generated, setGenerated] = useState<GenerateResult | null>(null);
  // The two-column template/action layout has a combined min-width of ~600px, so
  // below the desktop breakpoint it collapses to a single stacked column.
  const isCompact = useIsCompact();

  const libraryQuery = useQuery<FormTemplate[]>({
    queryKey: ['form-library', stateFilter, typeFilter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (stateFilter) params.set('state_code', stateFilter);
      if (typeFilter) params.set('type', typeFilter);
      const qs = params.toString();
      return api.get<FormTemplate[]>(`/api/form-templates/library${qs ? `?${qs}` : ''}`, signal);
    },
    retry: (n, e) => (e instanceof ApiAuthError ? false : n < 2),
  });

  const parcelsQuery = useQuery<Parcel[]>({
    queryKey: ['forms-parcel-picker', debouncedSearch],
    queryFn: ({ signal }) => {
      const qs = debouncedSearch ? `?q=${encodeURIComponent(debouncedSearch)}` : '';
      return api.get<Parcel[]>(`/api/parcels${qs}`, signal);
    },
    enabled: debouncedSearch.length > 0,
  });

  const selected = useMemo(
    () => (libraryQuery.data ?? []).find(t => t.id === selectedId) ?? null,
    [libraryQuery.data, selectedId],
  );

  const renderMutation = useMutation<RenderResult, Error, void>({
    mutationFn: () => api.post<RenderResult>(`/api/form-templates/${selectedId}/render`, {
      entity_type: selected?.entity_type ?? 'parcel',
      entity_id: selectedParcel!.id,
    }),
    onSuccess: (data) => { setPreview(data); setGenerated(null); },
    onError: (e) => toast({ variant: 'error', message: `Preview failed: ${e.message}` }),
  });

  const generateMutation = useMutation<GenerateResult, Error, void>({
    mutationFn: () => api.post<GenerateResult>(`/api/form-templates/${selectedId}/generate`, {
      entity_type: selected?.entity_type ?? 'parcel',
      entity_id: selectedParcel!.id,
    }),
    onSuccess: (data) => {
      setGenerated(data);
      toast({ variant: 'success', message: `Generated ${data.filename}` });
    },
    onError: (e) => toast({ variant: 'error', message: `Generation failed: ${e.message}` }),
  });

  const states = useMemo(() => {
    const set = new Set((libraryQuery.data ?? []).map(t => t.state_code));
    return Array.from(set).sort();
  }, [libraryQuery.data]);

  function selectTemplate(id: string) {
    setSelectedId(id);
    setPreview(null);
    setGenerated(null);
  }

  function pickParcel(p: Parcel) {
    setSelectedParcel(p);
    setParcelSearch('');
    setPreview(null);
    setGenerated(null);
  }

  const canAct = !!selectedId && !!selectedParcel;

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)', color: 'var(--color-ink)',
        }}>Forms library</h1>
        <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          Browse statutory forms, pick a parcel, and generate a merged PDF filed to the parcel's documents.
        </p>
      </header>

      {/* GM4 — surface toggle: statutory library vs. PDF Form Builder */}
      <div role="tablist" style={{ display: 'flex', gap: 'var(--sp-1)', marginBottom: 'var(--sp-5)', borderBottom: '1px solid var(--color-border)' }}>
        {([['library', 'Statutory library'], ['builder', 'Form builder (PDF)']] as const).map(([key, label]) => {
          const active = view === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setView(key)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 'var(--sp-2) var(--sp-3)', fontSize: 'var(--fs-sm)',
                fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                color: active ? 'var(--color-hunter)' : 'var(--color-ink-3)',
                borderBottom: `2px solid ${active ? 'var(--color-hunter)' : 'transparent'}`,
                marginBottom: -1,
              }}
            >{label}</button>
          );
        })}
      </div>

      {view === 'builder' ? <FormBuilderPanel /> : (
      <>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 180 }}>
          <span style={filterLabelStyle}>State</span>
          <Select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            options={[{ value: '', label: 'All states' }, ...states.map(s => ({ value: s, label: s }))]}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 180 }}>
          <span style={filterLabelStyle}>Type</span>
          <Select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            options={[
              { value: '', label: 'All types' },
              { value: 'authorization', label: 'Authorization' },
              { value: 'appeal', label: 'Appeal' },
              { value: 'notice', label: 'Notice' },
            ]}
          />
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(min(100%, 280px), 1fr) minmax(320px, 1.3fr)', gap: 'var(--sp-5)', alignItems: 'start' }}>
        {/* Template list */}
        <section>
          <h3 style={sectionHeadStyle}>Templates</h3>
          {libraryQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}><Spinner size="sm" /> Loading library…</p>}
          {libraryQuery.isError && <p style={{ color: 'var(--color-status-red)' }}>Failed to load the form library.</p>}
          {libraryQuery.data && libraryQuery.data.length === 0 && (
            <p style={{ color: 'var(--color-ink-3)' }}>No forms match the current filters.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {(libraryQuery.data ?? []).map(t => {
              const active = t.id === selectedId;
              return (
                <button
                  key={t.id}
                  onClick={() => selectTemplate(t.id)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', width: '100%',
                    background: active ? 'var(--color-celadon-bg)' : 'var(--color-card)',
                    border: `1px solid ${active ? 'var(--color-hunter)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--r-lg)', padding: 'var(--sp-3) var(--sp-4)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                    <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>{t.name}</span>
                    <Badge variant="neutral">{t.state_code}</Badge>
                  </div>
                  <div style={{ marginTop: 'var(--sp-2)', display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                    <Badge variant={typeVariant(t.type)}>{TYPE_LABELS[t.type] ?? t.type}</Badge>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>v{t.version} · {t.entity_type}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Action panel */}
        <section>
          <h3 style={sectionHeadStyle}>Generate</h3>
          <div style={{
            background: 'var(--color-card)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
          }}>
            {!selected ? (
              <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Select a form template to begin.</p>
            ) : (
              <>
                <div style={{ marginBottom: 'var(--sp-4)' }}>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)' }}>Selected form</div>
                  <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{selected.name}</div>
                </div>

                {/* Parcel picker */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', marginBottom: 'var(--sp-3)' }}>
                  <span style={filterLabelStyle}>Parcel</span>
                  {selectedParcel ? (
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: 'var(--sp-2) var(--sp-3)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--r-md)', background: 'var(--color-bg)',
                    }}>
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
                        {selectedParcel.account_number}{selectedParcel.owner_name ? ` · ${selectedParcel.owner_name}` : ''}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedParcel(null)}>Change</Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        placeholder="Search by account, name, or owner…"
                        value={parcelSearch}
                        onChange={e => setParcelSearch(e.target.value)}
                      />
                      {parcelsQuery.isFetching && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Searching…</span>}
                      {parcelsQuery.data && parcelsQuery.data.length > 0 && (
                        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', marginTop: 'var(--sp-1)', maxHeight: 200, overflowY: 'auto' }}>
                          {parcelsQuery.data.slice(0, 20).map(p => (
                            <button
                              key={p.id}
                              onClick={() => pickParcel(p)}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                                background: 'transparent', border: 'none', borderBottom: '1px solid var(--color-border)',
                                padding: 'var(--sp-2) var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
                              }}
                            >
                              <strong>{p.account_number}</strong>
                              {p.owner_name ? <span style={{ color: 'var(--color-ink-3)' }}> · {p.owner_name}</span> : null}
                              {p.county ? <span style={{ color: 'var(--color-ink-3)' }}> · {p.county}</span> : null}
                            </button>
                          ))}
                        </div>
                      )}
                      {parcelsQuery.data && debouncedSearch && parcelsQuery.data.length === 0 && !parcelsQuery.isFetching && (
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>No parcels found.</span>
                      )}
                    </>
                  )}
                </label>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                  <Button
                    variant="secondary"
                    disabled={!canAct || renderMutation.isPending}
                    onClick={() => renderMutation.mutate()}
                  >
                    {renderMutation.isPending ? 'Rendering…' : 'Preview'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!canAct || generateMutation.isPending}
                    onClick={() => generateMutation.mutate()}
                  >
                    {generateMutation.isPending ? 'Generating…' : 'Generate PDF'}
                  </Button>
                </div>

                {/* Generated download */}
                {generated && (
                  <div style={{
                    marginTop: 'var(--sp-4)', padding: 'var(--sp-3)', borderRadius: 'var(--r-md)',
                    background: 'var(--color-status-green-bg)', border: '1px solid var(--color-status-green)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)' }}>
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>{generated.filename}</span>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => downloadDocument(generated.document_id, (m) => toast({ variant: 'error', message: m }))}
                      >
                        Download PDF
                      </Button>
                    </div>
                    {generated.unresolved.length > 0 && (
                      <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                        {generated.unresolved.length} field(s) had no data: {generated.unresolved.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* Text preview */}
                {preview && (
                  <div style={{ marginTop: 'var(--sp-4)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-2)' }}>
                      <span style={filterLabelStyle}>Preview</span>
                      {preview.unresolved.length > 0 && (
                        <Badge variant="warning">{preview.unresolved.length} unresolved</Badge>
                      )}
                    </div>
                    <pre style={{
                      margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 'var(--fs-sm)',
                      background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--r-md)', padding: 'var(--sp-3)', color: 'var(--color-ink)',
                      maxHeight: 320, overflowY: 'auto',
                    }}>{preview.rendered_content}</pre>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      </>
      )}
    </>
  );
}

const filterLabelStyle: React.CSSProperties = {
  fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
  letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
};

const sectionHeadStyle: React.CSSProperties = {
  margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
  textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
};
